// components/map/BathymetryManager.js
import { useEffect, useRef, useCallback } from 'react';

const BathymetryManager = ({ 
  cesiumViewer, 
  isVisible = false,
}) => {
  const materialRef = useRef(null);
  const lightingListenerRef = useRef(null);
  const previousSceneSettingsRef = useRef(null);
  const isAppliedRef = useRef(false);

  // Apply enhanced bathymetry settings
  const applyBathymetrySettings = useCallback(() => {
    if (!cesiumViewer || !window.Cesium) return false;
    
    console.log('🌊 Applying enhanced bathymetry settings...');
    
    try {
      const scene = cesiumViewer.scene;
      const globe = scene.globe;
      
      // Enhanced settings
      globe.enableLighting = true;
      globe.maximumScreenSpaceError = 0.8; // High detail
      scene.verticalExaggeration = 4.0; // Dramatic depth
      
      // Scene has no `terrain` property. The active provider is exposed through
      // `terrainProvider` (and may briefly be undefined while Terrain resolves).
      // Never replace it here: an ellipsoid has zero height everywhere, which
      // makes the elevation ramp render the entire globe as one color.
      const terrainProvider = scene.terrainProvider;
      if (!terrainProvider) {
        console.log('⏳ Terrain provider is still loading');
      } else if (terrainProvider instanceof window.Cesium.EllipsoidTerrainProvider) {
        console.warn(
          '⚠️ Flat ellipsoid terrain is active; bathymetry color variation is unavailable.'
        );
      } else {
        console.log('✅ Terrain provider is available');
      }
      
      // Initial light direction (will be updated dynamically)
      scene.light = new window.Cesium.DirectionalLight({
        direction: new window.Cesium.Cartesian3(1, 0, 0),
        color: new window.Cesium.Color(1.0, 1.0, 1.0, 1.0),
        intensity: 1.5
      });
      
      console.log('✅ Enhanced bathymetry settings applied');
      return true;
      
    } catch (error) {
      console.warn('⚠️ Bathymetry settings failed:', error);
      return false;
    }
  }, [cesiumViewer]);

  // Add dynamic lighting that updates based on camera position
  const addDynamicLighting = useCallback(() => {
    if (!cesiumViewer || !window.Cesium) return;
    
    console.log('🌊 Adding dynamic lighting...');
    
    try {
      const scene = cesiumViewer.scene;
      const globe = scene.globe;
      const camera = scene.camera;
      const scratchNormal = new window.Cesium.Cartesian3();
      
      // Remove existing listener if any
      if (lightingListenerRef.current) {
        scene.preRender.removeEventListener(lightingListenerRef.current);
      }
      
      // Add pre-render listener for dynamic lighting
      const lightingListener = function (scene, time) {
        try {
          // Calculate surface normal at camera position
          const surfaceNormal = globe.ellipsoid.geodeticSurfaceNormal(
            camera.positionWC,
            scratchNormal,
          );
          const negativeNormal = window.Cesium.Cartesian3.negate(surfaceNormal, surfaceNormal);
          
          // Update light direction based on camera position
          scene.light.direction = window.Cesium.Cartesian3.normalize(
            window.Cesium.Cartesian3.add(negativeNormal, camera.rightWC, surfaceNormal),
            scene.light.direction,
          );
        } catch (lightingError) {
          // Fail silently to avoid console spam
        }
      };
      
      scene.preRender.addEventListener(lightingListener);
      lightingListenerRef.current = lightingListener;
      
      console.log('✅ Dynamic lighting enabled');
      
    } catch (error) {
      console.error('❌ Dynamic lighting failed:', error);
    }
  }, [cesiumViewer]);

  // Create bathymetry color ramp
  const createBathymetryColorRampFeet = (minHeightFeet, maxHeightFeet) => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 20;
    const ctx = canvas.getContext('2d');
    
    const range = maxHeightFeet - minHeightFeet;
    const d = (heightFeet) => (heightFeet - minHeightFeet) / range;
    
    const grd = ctx.createLinearGradient(0, 0, canvas.width, 0);
    
    // Color stops (your existing gradient)
    grd.addColorStop(d(maxHeightFeet), "#8B7355");
    grd.addColorStop(d(500.0), '#b59670');   
    grd.addColorStop(d(0.0), "#d9bb98");   
    grd.addColorStop(d(-16.0), "#f0d08c");
    grd.addColorStop(d(-33.0), "#DEB887");
    grd.addColorStop(d(-49.0), "#F0E68C");
    grd.addColorStop(d(-66.0), "#FFFF99");
    grd.addColorStop(d(-98.0), "#FFFF66");
    grd.addColorStop(d(-131.0), "#FFFF33");
    grd.addColorStop(d(-164.0), "#CCFF33");
    grd.addColorStop(d(-197.0), "#99FF33");
    grd.addColorStop(d(-230.0), "#66FF33");
    grd.addColorStop(d(-262.0), "#33FF33");
    grd.addColorStop(d(-295.0), "#33FF66");
    grd.addColorStop(d(-328.0), "#33FF99");
    grd.addColorStop(d(-394.0), "#33FFCC");
    grd.addColorStop(d(-459.0), "#33FFFF");
    grd.addColorStop(d(-525.0), "#33CCFF");
    grd.addColorStop(d(-590.0), "#3399FF");
    grd.addColorStop(d(-656.0), "#3366FF");
    grd.addColorStop(d(-722.0), "#3333FF");
    grd.addColorStop(d(-787.0), "#6633FF");
    grd.addColorStop(d(-853.0), "#9933FF");
    grd.addColorStop(d(-918.0), "#CC33FF");
    grd.addColorStop(d(-984.0), "#FF33FF");
    grd.addColorStop(d(-1312.0), "#FF3366");
    grd.addColorStop(d(-1640.0), "#FF3333");
    grd.addColorStop(d(-3281.0), "#CC0000");
    grd.addColorStop(d(minHeightFeet), "#330000");
    
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    return canvas;
  };

  // Create bathymetry material with contour lines
  const createBathymetryMaterial = useCallback(() => {
    if (!cesiumViewer || !window.Cesium) return null;
    
    console.log('🌊 Creating bathymetry material with contour lines...');
    
    try {
      const scene = cesiumViewer.scene;
      
      // Depth ranges for Gulf of Maine
      const minHeightFeet = -13123.0;
      const maxHeightFeet = 1000.0;
      const minHeightMeters = minHeightFeet / 3.28084;
      const maxHeightMeters = maxHeightFeet / 3.28084;
      
      // Create composite material
      const material = new window.Cesium.Material({
        fabric: {
          type: "ElevationColorContour",
          materials: {
            contourMaterial: {
              type: "ElevationContour",
            },
            elevationRampMaterial: {
              type: "ElevationRamp",
            },
          },
          components: {
            diffuse: "(1.0 - contourMaterial.alpha) * elevationRampMaterial.diffuse + contourMaterial.alpha * contourMaterial.diffuse",
            alpha: "max(contourMaterial.alpha, elevationRampMaterial.alpha)",
          },
        },
        translucent: false,
      });
      
      // Configure elevation color ramp
      const elevationUniforms = material.materials.elevationRampMaterial.uniforms;
      elevationUniforms.image = createBathymetryColorRampFeet(minHeightFeet, maxHeightFeet);
      elevationUniforms.minimumHeight = minHeightMeters * scene.verticalExaggeration;
      elevationUniforms.maximumHeight = maxHeightMeters * scene.verticalExaggeration;
      
      // Configure contour lines
      const contourUniforms = material.materials.contourMaterial.uniforms;
      contourUniforms.width = 1.0;
      contourUniforms.spacing = (25.0 / 3.28084) * scene.verticalExaggeration;
      contourUniforms.color = window.Cesium.Color.BLACK.withAlpha(0.4);
      
      console.log('✅ Bathymetry material created');
      return material;
      
    } catch (error) {
      console.error('❌ Bathymetry material creation failed:', error);
      return null;
    }
  }, [cesiumViewer]);

  // Show bathymetry
  const showBathymetry = useCallback(() => {
    if (!cesiumViewer || !window.Cesium) return false;
    
    console.log('🌊 Showing bathymetry...');
    
    try {
      const scene = cesiumViewer.scene;
      const globe = scene.globe;

      // Capture the complete pre-layer state once so deactivation can restore
      // the map exactly, including any material owned by another layer.
      if (!isAppliedRef.current) {
        previousSceneSettingsRef.current = {
          material: globe.material,
          enableLighting: globe.enableLighting,
          maximumScreenSpaceError: globe.maximumScreenSpaceError,
          verticalExaggeration: scene.verticalExaggeration,
          light: scene.light,
        };
        isAppliedRef.current = true;
      }

      // Apply settings
      applyBathymetrySettings();
      addDynamicLighting();
      
      // Create and apply material
      const material = createBathymetryMaterial();
      if (material) {
        cesiumViewer.scene.globe.material = material;
        materialRef.current = material;
      }
      
      console.log('✅ Bathymetry shown');
      return true;
      
    } catch (error) {
      console.error('❌ Failed to show bathymetry:', error);
      return false;
    }
  }, [cesiumViewer, applyBathymetrySettings, addDynamicLighting, createBathymetryMaterial]);

  // Hide bathymetry
  const hideBathymetry = useCallback(() => {
    if (!cesiumViewer) return;
    
    console.log('🌊 Hiding bathymetry...');
    
    try {
      const scene = cesiumViewer.scene;

      // Remove lighting listener
      if (lightingListenerRef.current) {
        scene.preRender.removeEventListener(lightingListenerRef.current);
        lightingListenerRef.current = null;
      }

      // Restore all state changed by this layer. Restoring the prior globe
      // material removes both the elevation colors and contour sub-material.
      if (isAppliedRef.current && previousSceneSettingsRef.current) {
        const previous = previousSceneSettingsRef.current;
        const globe = scene.globe;
        globe.material = previous.material;
        globe.enableLighting = previous.enableLighting;
        globe.maximumScreenSpaceError = previous.maximumScreenSpaceError;
        scene.verticalExaggeration = previous.verticalExaggeration;
        scene.light = previous.light;

        previousSceneSettingsRef.current = null;
        isAppliedRef.current = false;
        materialRef.current = null;
        scene.requestRender();
      }
      
      console.log('✅ Bathymetry hidden');
      
    } catch (error) {
      console.error('❌ Error hiding bathymetry:', error);
    }
  }, [cesiumViewer]);

  // Store latest functions in refs to avoid dependency issues
  const showBathymetryRef = useRef(showBathymetry);
  const hideBathymetryRef = useRef(hideBathymetry);
  
  useEffect(() => {
    showBathymetryRef.current = showBathymetry;
    hideBathymetryRef.current = hideBathymetry;
  }, [showBathymetry, hideBathymetry]);
  
  // Effect to handle visibility changes
  useEffect(() => {
    if (!cesiumViewer || !window.Cesium) return;
    
    // Check if scene is ready before accessing it (use try-catch since scene is a getter)
    try {
      if (!cesiumViewer.scene) {
        console.log('⏳ BathymetryManager: Waiting for Cesium scene to initialize...');
        return;
      }
    } catch (e) {
      console.log('⏳ BathymetryManager: Cesium viewer not fully initialized yet');
      return;
    }
    
    if (isVisible) {
      showBathymetryRef.current();
    } else {
      hideBathymetryRef.current();
    }
    
    // Cleanup on unmount - only hide if currently visible
    return () => {
      if (isVisible) {
        hideBathymetryRef.current();
      }
    };
  }, [cesiumViewer, isVisible]);

  return null; // This component doesn't render anything directly
};

export default BathymetryManager;
